import { RefObject, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as userSelectors } from 'builder/modules/user'
import { useI18n } from 'builder/hooks/useI18n'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { actions, SectionNames, selectors } from 'builder/modules/resumeEditor'
import { useRemainingAiWorkExperienceAttempts } from 'builder/hooks/useRemainingAiWorkExperienceAttempts'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { useContentAnimation } from 'builder/components/AiFeatures/AiProfileSummary'
import { Props } from '../types'
import { DIALOG_ANALYTICS_LABEL } from '../constants'
import { ANALYTICS_SECTION_NAME } from '../../../constants'

export const useAiWorkExperienceResult = (
  container: RefObject<HTMLElement>,
  suggestionRef: RefObject<HTMLElement>,
  props: Props,
) => {
  const { phrasesOpenerRef, editorContainerRef, currentWorkExperience } = props
  const isPremiumUser = useTypedSelector(userSelectors.premiumAccess)
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const { isResultModalOpen, summary: generatedContent } = useTypedSelector(
    selectors.aiWorkExperience,
  )
  const remainingAttempts = useRemainingAiWorkExperienceAttempts()
  const workExperienceId = currentWorkExperience.id
  const { isAnimationActive, animateContent } = useContentAnimation(suggestionRef, generatedContent)

  const attemptLeftText = useMemo(() => {
    if (remainingAttempts !== Infinity) {
      return i18n.t('builder.ai_profile_summary.rephrasing_modal.attempts_left', {
        attempts_left: remainingAttempts,
      })
    }
  }, [i18n, remainingAttempts])

  useClickOutsideExcept(container, [phrasesOpenerRef, editorContainerRef], () => {
    if (isResultModalOpen) {
      dispatch(actions.setIsAiWorkExperienceResultOpen(false))
      dispatch(actions.setAiWorkExperienceResultContentAnimation(false))
    }
  })

  useEscapeHandler(() => dispatch(actions.setIsAiWorkExperienceResultOpen(false)))

  const handleClose = useCallback(() => {
    dispatch(actions.resetAiWorkExperienceFlow())
    dispatch(actions.setIsAiWorkExperienceResultOpen(false))
    trackInternalEvent('click_cross', {
      label: DIALOG_ANALYTICS_LABEL,
      section: ANALYTICS_SECTION_NAME,
    })
  }, [dispatch])

  const handleRetry = useCallback(() => {
    if (remainingAttempts > 0) {
      dispatch(actions.setIsAiWorkExperienceResultOpen(false))
      dispatch(actions.setAiWorkExperienceResultContentAnimation(true))
      dispatch(actions.resetGenerateAiWorkExperienceStatus())
      dispatch(actions.generateResumeAiWorkExperience({ workExperienceId }))
    } else {
      dispatch(actions.setAiGenerationPaywallType('work_experience'))
    }
    trackInternalEvent('click_retry', {
      label: DIALOG_ANALYTICS_LABEL,
      section: ANALYTICS_SECTION_NAME,
    })
  }, [dispatch, remainingAttempts, workExperienceId])

  const handleUse = useCallback(() => {
    if (generatedContent) {
      dispatch(
        actions.updateCard({
          sectionId: SectionNames.workExperiences,
          cardId: workExperienceId,
          values: { description: generatedContent },
        }),
      )
      dispatch(actions.resetAiWorkExperienceFlow())
      dispatch(actions.setIsAiWorkExperienceResultOpen(false))
      trackInternalEvent('click_use', {
        label: DIALOG_ANALYTICS_LABEL,
        section: ANALYTICS_SECTION_NAME,
      })
    }
  }, [generatedContent, dispatch, workExperienceId])

  return {
    generatedContent,
    attemptLeftText,
    isPremiumUser,
    isAnimationActive,
    handleRetry,
    handleClose,
    animateContent,
    handleUse,
    remainingAttempts,
  }
}
